import { Dispatch, SetStateAction } from 'react'
import { cssVar } from 'theme/config'
import {
  AddVendorPayload,
  SSMAssetType,
  SSMGroupedData,
  SSMNistPillar,
  SSMResponseItem,
  SSMResponseNistsV2,
  SSMResponseProduct
} from '../../../models'
import { HeaderItemProps, CurrentCard } from './SSM'

export const nistPillars = [
  SSMNistPillar.Identify,
  SSMNistPillar.Protect,
  SSMNistPillar.Detect,
  SSMNistPillar.Respond,
  SSMNistPillar.Recover,
  SSMNistPillar.Govern
]

export const assetTypes = [
  SSMAssetType.Devices,
  SSMAssetType.Applications,
  SSMAssetType.Networks,
  SSMAssetType.Data,
  SSMAssetType.Users
]

export const groupSSMData = (data: SSMResponseNistsV2[], products: SSMResponseProduct[]): SSMGroupedData => {
  const groupedData: SSMGroupedData = []

  data.forEach((nist, index) => {
    const nistName = nistPillars[index]

    nist.forEach((asset, i) => {
      const assetName = assetTypes[i]

      const thisAsset = asset.map((currentAsset) => {
        const thisCurrentAsset = products.find((product) => product.uuid === currentAsset.itemId)

        if (thisCurrentAsset) {
          const currentAssetFromData = asset.find((n) => n.itemId === thisCurrentAsset.uuid)
          const cost =
            currentAssetFromData && Object.hasOwn(currentAssetFromData, 'cost')
              ? (currentAssetFromData as any).cost
              : thisCurrentAsset.cost || 0
          const costBps =
            currentAssetFromData && Object.hasOwn(currentAssetFromData, 'costBps')
              ? (currentAssetFromData as any).costBps
              : 0
          return { ...thisCurrentAsset, cost, costBps }
        }

        return null
      })

      groupedData.push({
        nist: nistName,
        asset: assetName,
        items: thisAsset as any
      })
    })
  })

  return groupedData
}

export const getHeaderItems = (groupedData: SSMGroupedData): Record<string, HeaderItemProps | number> => {
  const numOfDevices = []
  let devicesCost = 0
  let totalDeviceProducts = 0
  const numOfApplications = []
  let applicationsCost = 0
  let totalApplicationProducts = 0
  const numOfNetworks = []
  let networksCost = 0
  let totalNetworkProducts = 0
  const numOfUsers = []
  let usersCost = 0
  let totalUserProducts = 0
  const numOfData = []
  let dataCost = 0
  let totalDataProducts = 0

  groupedData.forEach((item) => {
    if (item.asset === 'Devices') {
      totalDeviceProducts += item.items.length
      if (item.items.length > 0) {
        numOfDevices.push(item)
        const cellTotalCost = item.items.reduce((acc: any, obj: any) => acc + obj.cost, 0)
        devicesCost += cellTotalCost
      }
    }
    if (item.asset === 'Applications') {
      totalApplicationProducts += item.items.length
      if (item.items.length > 0) {
        numOfApplications.push(item)
        const cellTotalCost = item.items.reduce((acc: any, obj: any) => acc + obj.cost, 0)
        applicationsCost += cellTotalCost
      }
    }
    if (item.asset === 'Networks') {
      totalNetworkProducts += item.items.length
      if (item.items.length > 0) {
        numOfNetworks.push(item)
        const cellTotalCost = item.items.reduce((acc: any, obj: any) => acc + obj.cost, 0)
        networksCost += cellTotalCost
      }
    }
    if (item.asset === 'Data') {
      totalDataProducts += item.items.length
      if (item.items.length > 0) {
        numOfData.push(item)
        const cellTotalCost = item.items.reduce((acc: any, obj: any) => acc + obj.cost, 0)
        dataCost += cellTotalCost
      }
    }
    if (item.asset === 'Users') {
      totalUserProducts += item.items.length
      if (item.items.length > 0) {
        numOfUsers.push(item)
        const cellTotalCost = item.items.reduce((acc: any, obj: any) => acc + obj.cost, 0)
        usersCost += cellTotalCost
      }
    }
  })

  return {
    devices: {
      headerCost: devicesCost,
      numOfItems: numOfDevices.length,
      totalProducts: totalDeviceProducts
    },
    applications: {
      headerCost: applicationsCost,
      numOfItems: numOfApplications.length,
      totalProducts: totalApplicationProducts
    },
    networks: {
      headerCost: networksCost,
      numOfItems: numOfNetworks.length,
      totalProducts: totalNetworkProducts
    },
    users: {
      headerCost: usersCost,
      numOfItems: numOfUsers.length,
      totalProducts: totalUserProducts
    },
    data: {
      headerCost: dataCost,
      numOfItems: numOfData.length,
      totalProducts: totalDataProducts
    },
    budgetTotalCost: devicesCost + applicationsCost + networksCost + usersCost + dataCost
  }
}

export const RECENTLY_ADDED_VENDORS = 'recently_added_vendors'
export const VENDOR_ADDED_AT = 'vendor_added_at'

export const recentSearchesSortFn = (a: any, b: any) => {
  if (a.index && b.index) {
    return a.index > b.index ? 1 : -1
  }
  return 1
}

export const addVendorToRecentSearches = (payload: any) => {
  if (!window || !window.localStorage) return

  const existingStoredVendors = localStorage.getItem(RECENTLY_ADDED_VENDORS)
  let recentlyAddedVendors: AddVendorPayload[] = []

  if (existingStoredVendors) {
    const existingParsed: AddVendorPayload[] = JSON.parse(existingStoredVendors)
    const vendorExists = existingParsed.find((n: any) => n.uuid === payload.uuid)

    const sortedArray = vendorExists
      ? existingParsed
          .map((n) =>
            (n as any).uuid === (vendorExists as any).uuid
              ? {
                  ...n,
                  index: 1
                }
              : {
                  ...n,
                  index: (n as any).index + 5
                }
          )
          .sort(recentSearchesSortFn)
          .map((n) => (n.index === 1 ? n : { ...n, index: n.index - 5 }))
      : [payload, ...existingParsed].sort(recentSearchesSortFn)

    if (existingParsed.length >= 5) {
      recentlyAddedVendors = sortedArray.slice(0, 5).map((n, i) => ({ ...n, index: i + 1 }))
    } else {
      recentlyAddedVendors = sortedArray.map((n, i) => ({ ...n, index: i + 1 }))
    }
  } else {
    recentlyAddedVendors = [{ ...payload, index: 1 }]
  }

  localStorage.setItem(RECENTLY_ADDED_VENDORS, JSON.stringify(recentlyAddedVendors))
  const date = new Date().getTime()
  localStorage.setItem(VENDOR_ADDED_AT, String(date))
}

export const updateVendorRecentSearches = (vendorId: string, values: any) => {
  if (!window || !window.localStorage) return

  const existingStoredVendors = localStorage.getItem(RECENTLY_ADDED_VENDORS)
  let recentlyAddedVendors: AddVendorPayload[] = []

  if (existingStoredVendors) {
    const existingParsed: AddVendorPayload[] = JSON.parse(existingStoredVendors)
    const vendorExists = existingParsed.find((n: any) => n.uuid === vendorId)

    let sortedArray = []

    if (vendorExists) {
      sortedArray = existingParsed
        .map((n) =>
          (n as any).uuid === (vendorExists as any).uuid
            ? {
                ...n,
                index: 1,
                ...values
              }
            : {
                ...n,
                index: (n as any).index + 5
              }
        )
        .sort(recentSearchesSortFn)
        .map((n) => (n.index === 1 ? n : { ...n, index: n.index - 5 }))
    }

    if (existingParsed.length >= 5) {
      recentlyAddedVendors = sortedArray.slice(0, 5).map((n, i) => ({ ...n, index: i + 1 }))
    } else {
      recentlyAddedVendors = sortedArray.map((n, i) => ({ ...n, index: i + 1 }))
    }
  }

  localStorage.setItem(RECENTLY_ADDED_VENDORS, JSON.stringify(recentlyAddedVendors))
  const date = new Date().getTime()
  localStorage.setItem(VENDOR_ADDED_AT, String(date))
}

export const getRecentSearches = () => {
  if (!window || !window.localStorage) return null

  const existingStoredVendors = localStorage.getItem(RECENTLY_ADDED_VENDORS)

  if (existingStoredVendors) {
    const result: AddVendorPayload = JSON.parse(existingStoredVendors)
    if (Array.isArray(result) && result.length > 0) return result
  }

  return null
}

export const findAllAddedVendors = (groupedData: SSMGroupedData) => {
  const allAddedVendors = nistPillars.map((nist) => {
    const assets = groupedData.filter((n) => n.nist === nist)
    const allAddedVendors: SSMResponseItem[] = []

    assets.forEach((asset) => {
      asset.items.forEach((assetItem) => {
        allAddedVendors.push(assetItem as any)
      })
    })

    return allAddedVendors
  })
  const allAddedVendorsFlat = allAddedVendors.flat()
  const allUniqueVendors: SSMResponseItem[] = []

  allAddedVendorsFlat.forEach((item) => {
    const hasItem = allUniqueVendors.find((n) => n.uuid === item.uuid)
    if (!hasItem) {
      allUniqueVendors.push(item)
    }
  })

  return allUniqueVendors
}

export const handleOpenCloseConditions = ({
  e,
  editMode,
  currentCard,
  setCurrentCard,
  customCard,
  setCustomCard,
  setDetailsCard,
  thisNist,
  thisAsset,
  currentTarget
}: {
  e: any
  editMode: boolean
  currentCard: CurrentCard
  setCurrentCard: Dispatch<SetStateAction<CurrentCard>>
  customCard: CurrentCard
  setCustomCard: Dispatch<SetStateAction<CurrentCard>>
  setDetailsCard: Dispatch<SetStateAction<CurrentCard>>
  thisNist: SSMNistPillar
  thisAsset: SSMAssetType
  currentTarget: EventTarget & HTMLDivElement
}) => {
  if (!editMode) {
    setDetailsCard({
      open: true,
      target: currentTarget,
      nist: thisNist,
      asset: thisAsset
    })
    e.preventDefault()

    return
  }

  if (!currentCard.target) {
    if (!currentCard.open) {
      setCurrentCard({
        open: true,
        nist: thisNist,
        asset: thisAsset,
        target: currentTarget
      })
    } else {
      setCurrentCard({
        open: false,
        nist: thisNist,
        asset: thisAsset,
        target: null
      })
    }

    return
  }

  if (customCard.open) {
    if (customCard.nist && customCard.nist === thisNist && customCard.asset && customCard.asset === thisAsset) {
      setCustomCard({
        open: false,
        nist: thisNist,
        asset: thisAsset
      })
    }
  }

  if (currentCard.open) {
    if (currentCard.nist && currentCard.nist === thisNist && currentCard.asset && currentCard.asset === thisAsset) {
      setCurrentCard({
        open: false,
        nist: thisNist,
        asset: thisAsset,
        target: currentTarget
      })

      return
    }

    setCurrentCard({
      open: true,
      nist: thisNist,
      asset: thisAsset,
      target: currentTarget
    })

    return
  }

  if (customCard.open) {
    setCustomCard({
      open: false,
      nist: thisNist,
      asset: thisAsset
    })

    if (currentCard.nist && currentCard.nist === thisNist && currentCard.asset && currentCard.asset === thisAsset) {
      setCurrentCard({
        open: false,
        nist: thisNist,
        asset: thisAsset,
        target: currentTarget
      })

      return
    } else {
      setCurrentCard({
        open: true,
        nist: thisNist,
        asset: thisAsset,
        target: currentTarget
      })
    }

    return
  }

  setCurrentCard({
    open: true,
    nist: thisNist,
    asset: thisAsset,
    target: currentTarget
  })
}

export const getNistPillarBackground = (nist: SSMNistPillar) => {
  switch (nist) {
    case SSMNistPillar.Identify:
      return cssVar('nist-identify')
    case SSMNistPillar.Protect:
      return cssVar('nist-protect')
    case SSMNistPillar.Detect:
      return cssVar('nist-detect')
    case SSMNistPillar.Respond:
      return cssVar('nist-respond')
    case SSMNistPillar.Recover:
      return cssVar('nist-recover')
    case SSMNistPillar.Govern:
      return cssVar('nist-govern')
    default:
      return 'e0e0e0'
  }
}

export const getBudgetBreakpoints = (maxCellValue: number): number[] => {
  const thresholdGap = maxCellValue / 5

  return [thresholdGap, thresholdGap * 2, thresholdGap * 3, thresholdGap * 4, thresholdGap * 5]
}

export const getAssetTypeColor = (assetType: SSMAssetType): string => {
  switch (assetType) {
    case SSMAssetType.Devices:
      return cssVar('asset-devices')
    case SSMAssetType.Applications:
      return '#7D5CE2'
    case SSMAssetType.Networks:
      return '#4073C2'
    case SSMAssetType.Data:
      return '#3D9C90'
    case SSMAssetType.Users:
      return '#968A2D'
    default:
      return '#e0e0e0'
  }
}

export const formatCostCompact = (value: number): string => {
  return Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 2
  }).format(value)
}

export const formatCostFull = (value: number): string => {
  return Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0
  }).format(value)
}

export enum CostDistribution {
  Even = 'even',
  Manual = 'manual'
}

export const findProductCells = (
  productUuid: string,
  groupedData: SSMGroupedData
): Array<{ nist: string; asset: string }> => {
  const affectedCells: Array<{ nist: string; asset: string }> = []

  groupedData.forEach((group) => {
    const hasProduct = group.items.some((item) => (item as any).uuid === productUuid)

    if (hasProduct) {
      affectedCells.push({
        nist: group.nist,
        asset: group.asset
      })
    }
  })

  return affectedCells
}
